import {
  Directive,
  effect,
  inject,
  input,
  OnDestroy,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';

import { Permission, PermissionStore } from '../../auth';

/**
 * Structural directive for permission-based visibility.
 * Shows or hides elements based on user permissions.
 *
 * @example
 * ```html
 * <!-- Hide element if user doesn't have permission -->
 * <button *appHasPermission="'transaction:cancel'">Cancel Transaction</button>
 *
 * <!-- With any permission from array -->
 * <div *appHasPermission="['transaction:view', 'transaction:create']">
 *   Transaction Section
 * </div>
 * ```
 *
 * @example Component usage
 * ```typescript
 * import { HasPermissionDirective } from 'shared';
 *
 * @Component({
 *   imports: [HasPermissionDirective],
 *   template: `
 *     <button *appHasPermission="'transaction:cancel'">Cancel</button>
 *   `
 * })
 * export class MyComponent {}
 * ```
 */
@Directive({
  selector: '[appHasPermission]',
  standalone: true,
})
export class HasPermissionDirective implements OnDestroy {
  private readonly templateRef = inject(TemplateRef<unknown>);
  private readonly viewContainer = inject(ViewContainerRef);
  private readonly permissionStore = inject(PermissionStore);

  private hasView = false;
  private currentPermission: Permission | Permission[] | null = null;
  private effectRef: ReturnType<typeof effect> | null = null;

  /**
   * Permission or array of permissions to check.
   * If array is provided, user needs at least one permission (OR logic).
   */
  appHasPermission = input.required<Permission | Permission[]>();

  constructor() {
    // Use effect to react to permission changes
    this.effectRef = effect(() => {
      // Read the permission input
      const permission = this.appHasPermission();
      this.currentPermission = permission;

      // Access permissions signal to track changes
      this.permissionStore.permissions();

      // Update the view based on permission
      this.updateView();
    });
  }

  private updateView(): void {
    if (!this.currentPermission) return;

    const hasPermission = this.checkPermission();

    if (hasPermission && !this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (!hasPermission && this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }

  private checkPermission(): boolean {
    if (!this.currentPermission) return false;

    if (Array.isArray(this.currentPermission)) {
      // OR logic - user needs at least one permission
      return this.permissionStore.hasAnyPermission(this.currentPermission);
    }

    return this.permissionStore.hasPermission(this.currentPermission);
  }

  ngOnDestroy(): void {
    // Effect is automatically cleaned up by Angular, but we clear the view
    this.viewContainer.clear();
    this.hasView = false;
  }
}
